// Learned median-interval recurrence. Pure — no I/O, and no Date.now() except
// through the injectable `nowIso` so tests can freeze "today".
//
// Day math walks YYYY-MM-DD strings with Date.UTC (see datetime.ts) so a DST
// transition never shifts a day boundary. Intervals are whole calendar days
// in the household timezone, not millisecond deltas.

import { daysBetween, localCalendarDate } from "./datetime.js";

export const MIN_OCCURRENCES_FOR_INTERVAL = 3;
export const DUE_SOON_FRACTION = 0.8;
export const OVERDUE_FRACTION = 1.25;
export const OVERDUE_MIN_ABSOLUTE_DAYS = 1;
export const REGULARITY_FLOOR = 0;

export interface RecurrenceStats {
  occurrenceCount: number;
  firstAt?: string;
  lastAt?: string;
  /** undefined when occurrenceCount < MIN_OCCURRENCES_FOR_INTERVAL. */
  medianIntervalDays?: number;
  meanIntervalDays?: number;
  intervalStdDevDays?: number;
  regularity?: number;                  // 0..1
  daysSinceLast?: number;
  dueStatus: "unknown" | "ok" | "due_soon" | "overdue";
  overdueByDays?: number;               // only when dueStatus === "overdue"
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid]!;
  return (sorted[mid - 1]! + sorted[mid]!) / 2;
}

function mean(values: number[]): number {
  return values.reduce((sum, n) => sum + n, 0) / values.length;
}

/** Population standard deviation. Identical gaps → 0, so regularity → 1. */
function stdDev(values: number[], avg: number): number {
  if (values.length === 0) return 0;
  const variance = values.reduce((sum, n) => sum + (n - avg) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export function computeRecurrence(
  occurredAtIsoList: string[],
  timezone: string,
  nowIso?: string,
): RecurrenceStats {
  const now = nowIso ?? new Date().toISOString();
  const sorted = [...occurredAtIsoList]
    .filter((iso) => !Number.isNaN(new Date(iso).getTime()))
    .sort((a, b) => a.localeCompare(b));

  if (sorted.length === 0) {
    return { occurrenceCount: 0, dueStatus: "unknown" };
  }

  const firstAt = sorted[0]!;
  const lastAt = sorted[sorted.length - 1]!;
  const lastDate = localCalendarDate(lastAt, timezone);
  const nowDate = localCalendarDate(now, timezone);
  const daysSinceLast = daysBetween(lastDate, nowDate);

  const base: RecurrenceStats = {
    occurrenceCount: sorted.length,
    firstAt,
    lastAt,
    daysSinceLast,
    dueStatus: "unknown",
  };

  if (sorted.length < MIN_OCCURRENCES_FOR_INTERVAL) return base;

  const dates = sorted.map((iso) => localCalendarDate(iso, timezone));
  const gaps: number[] = [];
  for (let i = 1; i < dates.length; i++) {
    gaps.push(daysBetween(dates[i - 1]!, dates[i]!));
  }

  const medianIntervalDays = median(gaps);
  const meanIntervalDays = mean(gaps);
  const intervalStdDevDays = stdDev(gaps, meanIntervalDays);
  const regularity =
    meanIntervalDays === 0
      ? REGULARITY_FLOOR
      : Math.max(REGULARITY_FLOOR, 1 - intervalStdDevDays / meanIntervalDays);

  let dueStatus: RecurrenceStats["dueStatus"] = "ok";
  let overdueByDays: number | undefined;
  if (
    daysSinceLast > medianIntervalDays * OVERDUE_FRACTION &&
    daysSinceLast > medianIntervalDays + OVERDUE_MIN_ABSOLUTE_DAYS
  ) {
    dueStatus = "overdue";
    overdueByDays = daysSinceLast - medianIntervalDays;
  } else if (daysSinceLast >= medianIntervalDays * DUE_SOON_FRACTION) {
    dueStatus = "due_soon";
  }

  return {
    ...base,
    medianIntervalDays,
    meanIntervalDays,
    intervalStdDevDays,
    regularity,
    dueStatus,
    overdueByDays,
  };
}
